import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Download, Printer, Save, FileText, Search, Copy, Loader2, Receipt, CheckCircle2, X, FileSpreadsheet, Calendar } from 'lucide-react';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().split('T')[0];

const blankItem = () => ({ _uid: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `it-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, description: '', hsn_sac: '999293', qty: 1, rate: 0 });
const blankInvoice = () => ({
  bill_to: { name: '', email: '', phone: '', address: '', gstin: '', state: 'West Bengal' },
  items: [blankItem()],
  discount_amount: 0,
  gst_pct: 18,
  invoice_date: today(),
  due_date: '',
  payment_mode: 'upi',
  payment_reference: '',
  notes: '',
  status: 'draft',
});

const statusColor = (s) => ({
  draft: 'bg-slate-100 text-slate-700',
  issued: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  cancelled: 'bg-rose-100 text-rose-700',
}[s] || 'bg-slate-100 text-slate-700');

export default function InvoiceGeneratorPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [selected, setSelected] = useState(null); // full invoice doc (with id/invoice_number)
  const [form, setForm] = useState(blankInvoice());
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const debounceRef = useRef(null);

  // Monthly GST report state
  const [reportMonth, setReportMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [reportSummary, setReportSummary] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportDownloading, setReportDownloading] = useState('');

  const fetchReportSummary = useCallback(async (month) => {
    setReportLoading(true);
    try {
      const { data } = await api.get('/invoices/reports/monthly-gst', { params: { month } });
      setReportSummary(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || 'Failed to load GST summary');
      setReportSummary(null);
    } finally { setReportLoading(false); }
  }, []);

  useEffect(() => { if (reportMonth) fetchReportSummary(reportMonth); }, [reportMonth, fetchReportSummary]);

  const downloadReport = async (format) => {
    setReportDownloading(format);
    try {
      const url = format === 'csv' ? '/invoices/reports/monthly-gst.csv' : '/invoices/reports/monthly-gst.pdf';
      const res = await api.get(url, { params: { month: reportMonth }, responseType: 'blob' });
      const mime = format === 'csv' ? 'text/csv' : 'application/pdf';
      const blob = new Blob([res.data], { type: mime });
      const link = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = link; a.download = `gst-report-${reportMonth}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(link);
      toast.success(`GST report (${format.toUpperCase()}) downloaded`);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || 'Failed to download GST report');
    } finally { setReportDownloading(''); }
  };

  // Fetch list
  const fetchList = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (q) params.q = q;
      const { data } = await api.get('/invoices', { params });
      setInvoices(data || []);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setLoading(false); }
  }, [q, filterStatus]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Live totals (for preview panel)
  const totals = useMemo(() => {
    const subtotal = (form.items || []).reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.rate) || 0), 0);
    const disc = Number(form.discount_amount) || 0;
    const taxable = Math.max(subtotal - disc, 0);
    const buyerState = (form.bill_to?.state || '').trim().toLowerCase();
    const intra = !buyerState || buyerState === 'west bengal';
    const gst = taxable * (Number(form.gst_pct) || 0) / 100;
    const cgst = intra ? gst / 2 : 0;
    const sgst = intra ? gst - cgst : 0;
    const igst = intra ? 0 : gst;
    return {
      subtotal, discount: disc, taxable,
      cgst, sgst, igst, total_gst: cgst + sgst + igst,
      grand_total: taxable + gst,
      intra,
    };
  }, [form]);

  // Auto-save (debounced 900ms) when editing an existing invoice
  useEffect(() => {
    if (!selected?.id || !dirty) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setSaving(true);
        const { data } = await api.patch(`/invoices/${selected.id}`, form);
        setSelected(data);
        setLastSavedAt(new Date());
        setDirty(false);
        fetchList();
      } catch (err) {
        toast.error(formatError(err.response?.data?.detail) || 'Auto-save failed');
      } finally { setSaving(false); }
    }, 900);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [form, selected, dirty, fetchList]);

  const startNew = async () => {
    // Create a fresh draft server-side so we get an invoice_number immediately
    try {
      setSaving(true);
      const fresh = blankInvoice();
      const { data } = await api.post('/invoices', fresh);
      setSelected(data);
      setForm({
        bill_to: data.bill_to || fresh.bill_to,
        items: data.items?.length ? data.items : [blankItem()],
        discount_amount: data.discount_amount || 0,
        gst_pct: data.gst_pct || 18,
        invoice_date: data.invoice_date || today(),
        due_date: data.due_date || '',
        payment_mode: data.payment_mode || 'upi',
        payment_reference: data.payment_reference || '',
        notes: data.notes || '',
        status: data.status || 'draft',
      });
      setDirty(false);
      setLastSavedAt(new Date());
      fetchList();
      toast.success(`Draft ${data.invoice_number} created`);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const openInvoice = async (id) => {
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setSelected(data);
      setForm({
        bill_to: data.bill_to || blankInvoice().bill_to,
        items: data.items?.length ? data.items : [blankItem()],
        discount_amount: data.discount_amount || 0,
        gst_pct: data.gst_pct || 18,
        invoice_date: data.invoice_date || today(),
        due_date: data.due_date || '',
        payment_mode: data.payment_mode || 'upi',
        payment_reference: data.payment_reference || '',
        notes: data.notes || '',
        status: data.status || 'draft',
      });
      setDirty(false);
      setLastSavedAt(new Date(data.updated_at));
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const patch = (updater) => {
    setForm(prev => {
      const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      return next;
    });
    setDirty(true);
  };

  const setBillTo = (patchObj) => patch(prev => ({ ...prev, bill_to: { ...prev.bill_to, ...patchObj } }));
  const setItem = (idx, patchObj) => patch(prev => {
    const items = [...prev.items];
    items[idx] = { ...items[idx], ...patchObj };
    return { ...prev, items };
  });
  const addItem = () => patch(prev => ({ ...prev, items: [...prev.items, blankItem()] }));
  const removeItem = (idx) => patch(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));

  const saveNow = async () => {
    if (!selected?.id) return;
    try {
      setSaving(true);
      const { data } = await api.patch(`/invoices/${selected.id}`, form);
      setSelected(data);
      setLastSavedAt(new Date());
      setDirty(false);
      toast.success('Invoice saved');
      fetchList();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const downloadPdf = async () => {
    if (!selected?.id) return;
    // ensure latest saved
    if (dirty) await saveNow();
    setDownloading(true);
    try {
      const res = await api.get(`/invoices/${selected.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `invoice-${selected.invoice_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || 'Failed to download');
    } finally { setDownloading(false); }
  };

  const printPdf = async () => {
    if (!selected?.id) return;
    if (dirty) await saveNow();
    try {
      const res = await api.get(`/invoices/${selected.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const win = window.open(url, '_blank');
      if (win) {
        win.addEventListener('load', () => { try { win.focus(); win.print(); } catch (err) { console.warn('Print blocked by browser:', err); } });
      } else {
        toast.error('Popup blocked — please allow popups to print');
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || 'Failed to open print');
    }
  };

  const issueInvoice = async () => {
    if (!selected?.id) return;
    try {
      await api.post(`/invoices/${selected.id}/issue`);
      toast.success('Invoice issued');
      openInvoice(selected.id);
      fetchList();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const markPaid = async () => {
    if (!selected?.id) return;
    patch({ status: 'paid' });
    setTimeout(saveNow, 100);
  };

  const deleteInvoice = async () => {
    if (!selected?.id) return;
    if (!window.confirm(`Delete invoice ${selected.invoice_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/invoices/${selected.id}`);
      toast.success('Invoice deleted');
      setSelected(null);
      setForm(blankInvoice());
      fetchList();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const duplicateInvoice = async () => {
    if (!selected?.id) return;
    try {
      setSaving(true);
      const clone = { ...form, status: 'draft' };
      const { data } = await api.post('/invoices', clone);
      setSelected(data);
      setLastSavedAt(new Date());
      setDirty(false);
      toast.success(`Duplicated as ${data.invoice_number}`);
      fetchList();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="invoice-generator-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center">
            <Receipt size={20} className="text-white" />
          </div>
          <div>
            <h1 className="font-heading text-xl font-bold text-brand-text-primary">Invoice Generator</h1>
            <p className="text-xs text-brand-text-secondary">Create, auto-save, print and download GST-compliant invoices</p>
          </div>
        </div>
        <Button onClick={startNew} disabled={saving} className="bg-brand-primary text-white" data-testid="new-invoice-btn">
          {saving && !selected ? <Loader2 size={14} className="animate-spin mr-1" /> : <Plus size={14} className="mr-1" />}
          New Invoice
        </Button>
      </div>

      {/* MONTHLY GST REPORT */}
      <Card className="border-brand-border bg-white" data-testid="gst-report-card">
        <CardContent className="p-4">
          <div className="flex items-center flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center">
                <Calendar size={16} className="text-white" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">Monthly GST Report</p>
                <p className="text-sm font-heading font-bold text-brand-text-primary">GSTR-1 / GSTR-3B Register</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs">Month</Label>
              <Input
                type="month"
                value={reportMonth}
                onChange={e => setReportMonth(e.target.value)}
                className="h-9 text-sm w-40"
                data-testid="gst-month"
              />
            </div>

            {reportLoading ? (
              <Loader2 size={14} className="animate-spin text-brand-primary" />
            ) : reportSummary && (
              <div className="flex items-center flex-wrap gap-3 text-xs mr-auto" data-testid="gst-summary">
                <span className="px-2 py-1 rounded bg-brand-bg text-brand-text-secondary">
                  Invoices <span className="font-mono font-bold text-brand-text-primary ml-1">{reportSummary.invoice_count}</span>
                </span>
                <span className="px-2 py-1 rounded bg-brand-bg text-brand-text-secondary">
                  Taxable <span className="font-mono font-bold text-brand-text-primary ml-1">{money(reportSummary.totals?.taxable)}</span>
                </span>
                <span className="px-2 py-1 rounded bg-brand-bg text-brand-text-secondary">
                  CGST+SGST <span className="font-mono font-bold text-brand-text-primary ml-1">{money((reportSummary.totals?.cgst || 0) + (reportSummary.totals?.sgst || 0))}</span>
                </span>
                <span className="px-2 py-1 rounded bg-brand-bg text-brand-text-secondary">
                  IGST <span className="font-mono font-bold text-brand-text-primary ml-1">{money(reportSummary.totals?.igst)}</span>
                </span>
                <span className="px-2 py-1 rounded bg-slate-900 text-white">
                  Grand <span className="font-mono font-bold text-white ml-1" data-testid="gst-grand-total">{money(reportSummary.totals?.grand)}</span>
                </span>
              </div>
            )}

            <div className="flex gap-2 ml-auto">
              <Button
                size="sm" variant="outline"
                onClick={() => downloadReport('csv')}
                disabled={!!reportDownloading}
                className="text-xs h-9"
                data-testid="download-gst-csv"
              >
                {reportDownloading === 'csv' ? <Loader2 size={12} className="animate-spin mr-1" /> : <FileSpreadsheet size={12} className="mr-1" />}
                CSV
              </Button>
              <Button
                size="sm"
                onClick={() => downloadReport('pdf')}
                disabled={!!reportDownloading}
                className="bg-brand-primary text-white text-xs h-9"
                data-testid="download-gst-pdf"
              >
                {reportDownloading === 'pdf' ? <Loader2 size={12} className="animate-spin mr-1" /> : <Download size={12} className="mr-1" />}
                Download GST PDF
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4">
        {/* LEFT: Invoice list */}
        <Card className="border-brand-border bg-white h-fit">
          <CardHeader className="pb-2 border-b border-brand-border">
            <CardTitle className="text-sm flex items-center gap-2"><FileText size={14} /> Invoices</CardTitle>
            <div className="flex gap-2 mt-2">
              <div className="relative flex-1">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-brand-text-secondary" />
                <Input placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} className="h-8 text-xs pl-7" data-testid="invoice-search" />
              </div>
              <Select value={filterStatus || 'all'} onValueChange={v => setFilterStatus(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-8 text-xs w-24" data-testid="filter-status"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="issued">Issued</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-0 max-h-[70vh] overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center"><Loader2 size={20} className="animate-spin mx-auto text-brand-primary" /></div>
            ) : invoices.length === 0 ? (
              <div className="p-8 text-center text-xs text-brand-text-secondary">No invoices yet. Click <span className="font-semibold">New Invoice</span> to start.</div>
            ) : (
              <ul className="divide-y divide-brand-border">
                {invoices.map(inv => (
                  <li
                    key={inv.id}
                    onClick={() => openInvoice(inv.id)}
                    className={`p-3 cursor-pointer hover:bg-brand-bg/60 transition-colors ${selected?.id === inv.id ? 'bg-brand-primary/5 border-l-2 border-brand-primary' : ''}`}
                    data-testid={`invoice-list-item-${inv.id}`}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0">
                        <p className="font-mono text-[11px] font-bold text-brand-text-primary truncate">{inv.invoice_number}</p>
                        <p className="text-xs text-brand-text-primary truncate">{inv.bill_to?.name || 'Untitled'}</p>
                        <p className="text-[10px] text-brand-text-secondary">{inv.invoice_date}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-xs font-bold text-brand-text-primary">{money(inv.grand_total)}</p>
                        <Badge className={`text-[9px] border-0 mt-1 ${statusColor(inv.status)}`}>{(inv.status || 'draft').toUpperCase()}</Badge>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* RIGHT: Editor */}
        {!selected ? (
          <Card className="border-brand-border bg-white">
            <CardContent className="p-16 text-center">
              <Receipt size={48} className="mx-auto text-brand-text-secondary/40 mb-4" />
              <p className="text-brand-text-primary font-semibold mb-1">No invoice selected</p>
              <p className="text-sm text-brand-text-secondary mb-4">Select an invoice from the left, or create a new one.</p>
              <Button onClick={startNew} disabled={saving} className="bg-brand-primary text-white" data-testid="new-invoice-empty-btn">
                <Plus size={14} className="mr-1" /> Create New Invoice
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Toolbar */}
            <Card className="border-brand-border bg-white">
              <CardContent className="p-3 flex items-center flex-wrap gap-2">
                <div className="flex items-center gap-2 mr-auto">
                  <span className="font-mono text-sm font-bold text-brand-text-primary" data-testid="editor-invoice-number">{selected.invoice_number}</span>
                  <Badge className={`text-[10px] border-0 ${statusColor(form.status)}`}>{(form.status || 'draft').toUpperCase()}</Badge>
                  <span className="text-[11px] text-brand-text-secondary">
                    {saving ? (
                      <span className="flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> Saving…</span>
                    ) : dirty ? (
                      <span className="text-amber-600">Unsaved changes</span>
                    ) : lastSavedAt ? (
                      <span className="flex items-center gap-1 text-green-700"><CheckCircle2 size={10} /> Saved {lastSavedAt.toLocaleTimeString()}</span>
                    ) : null}
                  </span>
                </div>
                <Button size="sm" variant="outline" onClick={saveNow} disabled={saving} className="text-xs h-8" data-testid="save-btn"><Save size={12} className="mr-1" /> Save</Button>
                <Button size="sm" variant="outline" onClick={duplicateInvoice} disabled={saving} className="text-xs h-8" data-testid="duplicate-btn"><Copy size={12} className="mr-1" /> Duplicate</Button>
                <Button size="sm" variant="outline" onClick={printPdf} className="text-xs h-8" data-testid="print-btn"><Printer size={12} className="mr-1" /> Print</Button>
                <Button size="sm" onClick={downloadPdf} disabled={downloading} className="bg-brand-primary text-white text-xs h-8" data-testid="download-pdf-btn">
                  {downloading ? <Loader2 size={12} className="animate-spin mr-1" /> : <Download size={12} className="mr-1" />} PDF
                </Button>
                {form.status === 'draft' && (
                  <Button size="sm" onClick={issueInvoice} className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8" data-testid="issue-btn">Issue</Button>
                )}
                {form.status !== 'paid' && (
                  <Button size="sm" onClick={markPaid} className="bg-green-600 hover:bg-green-700 text-white text-xs h-8" data-testid="mark-paid-btn">Mark Paid</Button>
                )}
                <Button size="sm" variant="outline" onClick={deleteInvoice} className="text-xs h-8 text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="delete-btn"><Trash2 size={12} className="mr-1" /> Delete</Button>
              </CardContent>
            </Card>

            {/* Editor grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* BILL TO */}
              <Card className="border-brand-border bg-white">
                <CardHeader className="pb-2 border-b border-brand-border"><CardTitle className="text-sm">Bill To</CardTitle></CardHeader>
                <CardContent className="p-4 space-y-3">
                  <div><Label className="text-xs">Name / Company</Label><Input value={form.bill_to.name} onChange={e => setBillTo({ name: e.target.value })} className="h-8 text-sm" data-testid="bill-to-name" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">Email</Label><Input value={form.bill_to.email} onChange={e => setBillTo({ email: e.target.value })} className="h-8 text-sm" data-testid="bill-to-email" /></div>
                    <div><Label className="text-xs">Phone</Label><Input value={form.bill_to.phone} onChange={e => setBillTo({ phone: e.target.value })} className="h-8 text-sm" data-testid="bill-to-phone" /></div>
                  </div>
                  <div><Label className="text-xs">Address</Label><Textarea value={form.bill_to.address} onChange={e => setBillTo({ address: e.target.value })} rows={2} className="text-sm" data-testid="bill-to-address" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">State</Label><Input value={form.bill_to.state} onChange={e => setBillTo({ state: e.target.value })} className="h-8 text-sm" data-testid="bill-to-state" /></div>
                    <div><Label className="text-xs">GSTIN (optional)</Label><Input value={form.bill_to.gstin} onChange={e => setBillTo({ gstin: e.target.value.toUpperCase() })} className="h-8 text-sm font-mono uppercase" data-testid="bill-to-gstin" /></div>
                  </div>
                </CardContent>
              </Card>

              {/* META */}
              <Card className="border-brand-border bg-white">
                <CardHeader className="pb-2 border-b border-brand-border"><CardTitle className="text-sm">Invoice Details</CardTitle></CardHeader>
                <CardContent className="p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">Invoice Date</Label><Input type="date" value={form.invoice_date} onChange={e => patch({ invoice_date: e.target.value })} className="h-8 text-sm" data-testid="invoice-date" /></div>
                    <div><Label className="text-xs">Due Date</Label><Input type="date" value={form.due_date} onChange={e => patch({ due_date: e.target.value })} className="h-8 text-sm" data-testid="due-date" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Payment Mode</Label>
                      <Select value={form.payment_mode} onValueChange={v => patch({ payment_mode: v })}>
                        <SelectTrigger className="h-8 text-sm" data-testid="payment-mode"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="upi">UPI</SelectItem>
                          <SelectItem value="bank">Bank Transfer</SelectItem>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="cheque">Cheque</SelectItem>
                          <SelectItem value="card">Card</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">Payment Ref</Label><Input value={form.payment_reference} onChange={e => patch({ payment_reference: e.target.value })} className="h-8 text-sm" data-testid="payment-ref" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">Discount (₹)</Label><Input type="number" min="0" value={form.discount_amount} onChange={e => patch({ discount_amount: Number(e.target.value) || 0 })} className="h-8 text-sm" data-testid="discount" /></div>
                    <div><Label className="text-xs">GST %</Label><Input type="number" min="0" max="28" value={form.gst_pct} onChange={e => patch({ gst_pct: Number(e.target.value) || 0 })} className="h-8 text-sm" data-testid="gst-pct" /></div>
                  </div>
                  <div><Label className="text-xs">Notes / Remarks</Label><Textarea value={form.notes} onChange={e => patch({ notes: e.target.value })} rows={2} className="text-sm" data-testid="notes" /></div>
                </CardContent>
              </Card>
            </div>

            {/* LINE ITEMS */}
            <Card className="border-brand-border bg-white">
              <CardHeader className="pb-2 border-b border-brand-border flex flex-row items-center justify-between">
                <CardTitle className="text-sm">Line Items</CardTitle>
                <Button size="sm" variant="outline" onClick={addItem} className="text-xs h-7" data-testid="add-item-btn"><Plus size={12} className="mr-1" /> Add Item</Button>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-brand-border bg-brand-bg/50">
                      {['Description', 'HSN/SAC', 'Qty', 'Rate (₹)', 'Amount (₹)', ''].map((h, i) => (
                        <th key={h || `col-${i}`} className="text-left py-2 px-3 text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, idx) => {
                      const amt = (Number(it.qty) || 0) * (Number(it.rate) || 0);
                      return (
                        <tr key={it._uid || `item-${idx}`} className="border-b border-brand-border/50" data-testid={`item-row-${idx}`}>
                          <td className="py-1 px-3"><Input value={it.description} onChange={e => setItem(idx, { description: e.target.value })} placeholder="Item description" className="h-8 text-sm border-0 focus-visible:ring-1" data-testid={`item-desc-${idx}`} /></td>
                          <td className="py-1 px-3 w-28"><Input value={it.hsn_sac} onChange={e => setItem(idx, { hsn_sac: e.target.value })} className="h-8 text-sm border-0 focus-visible:ring-1 font-mono" data-testid={`item-hsn-${idx}`} /></td>
                          <td className="py-1 px-3 w-20"><Input type="number" min="0" step="0.01" value={it.qty} onChange={e => setItem(idx, { qty: Number(e.target.value) || 0 })} className="h-8 text-sm border-0 focus-visible:ring-1 text-right font-mono" data-testid={`item-qty-${idx}`} /></td>
                          <td className="py-1 px-3 w-28"><Input type="number" min="0" step="0.01" value={it.rate} onChange={e => setItem(idx, { rate: Number(e.target.value) || 0 })} className="h-8 text-sm border-0 focus-visible:ring-1 text-right font-mono" data-testid={`item-rate-${idx}`} /></td>
                          <td className="py-1 px-3 w-32 text-right font-mono font-semibold text-brand-text-primary" data-testid={`item-amount-${idx}`}>{money(amt)}</td>
                          <td className="py-1 px-3 w-10">
                            {form.items.length > 1 && (
                              <button onClick={() => removeItem(idx)} className="text-rose-500 hover:text-rose-700" data-testid={`item-remove-${idx}`}><X size={14} /></button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* SUMMARY */}
            <Card className="border-brand-border bg-white">
              <CardContent className="p-4">
                <div className="flex justify-end">
                  <div className="w-full max-w-sm space-y-1 text-sm">
                    <div className="flex justify-between py-1 border-b border-brand-border/40"><span className="text-brand-text-secondary">Subtotal</span><span className="font-mono">{money(totals.subtotal)}</span></div>
                    {totals.discount > 0 && <div className="flex justify-between py-1 border-b border-brand-border/40"><span className="text-brand-text-secondary">Discount</span><span className="font-mono text-rose-600">- {money(totals.discount)}</span></div>}
                    <div className="flex justify-between py-1 border-b border-brand-border/40"><span className="text-brand-text-secondary">Taxable Value</span><span className="font-mono">{money(totals.taxable)}</span></div>
                    {totals.intra ? (
                      <>
                        <div className="flex justify-between py-1 border-b border-brand-border/40"><span className="text-brand-text-secondary">CGST @ {(form.gst_pct / 2).toFixed(2)}%</span><span className="font-mono">{money(totals.cgst)}</span></div>
                        <div className="flex justify-between py-1 border-b border-brand-border/40"><span className="text-brand-text-secondary">SGST @ {(form.gst_pct / 2).toFixed(2)}%</span><span className="font-mono">{money(totals.sgst)}</span></div>
                      </>
                    ) : (
                      <div className="flex justify-between py-1 border-b border-brand-border/40"><span className="text-brand-text-secondary">IGST @ {form.gst_pct}%</span><span className="font-mono">{money(totals.igst)}</span></div>
                    )}
                    <div className="flex justify-between py-2 mt-1 bg-slate-900 text-white px-3 rounded"><span className="font-bold">GRAND TOTAL</span><span className="font-mono font-bold" data-testid="grand-total">{money(totals.grand_total)}</span></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
